import { useQuery } from "@apollo/client"
import React from "react"
import invariant from "tiny-invariant"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { LoadingSpinner } from "~/ui/loading-spinner"
import { useToast } from "~/ui/use-toast"
import { GroupIndexItem } from "./group-index-item"

import { LEAVE_GROUP_MUTATION } from "./group-mutations"

const GROUPS_QUERY = gql(/* GraphQL */ `
  query GroupsQuery($after: String) {
    groups(first: 50, after: $after, filters: { preset: MEMBER }) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...GroupIndexItem
          shareToken
        }
      }
    }
  }
`)

export const GroupIndexScreen = () => {
  const { data, loading, error, fetchMore } = useQuery(GROUPS_QUERY)
  const { toast } = useToast()
  const [leaveGroupDialog, setLeaveGroupDialog] = React.useState<{
    id: string
    name: string
  } | null>(null)

  const [leaveGroup] = useSafeMutation(LEAVE_GROUP_MUTATION, {
    refetchQueries: ["GroupsQuery"],
    onError: (error) => {
      const isLastAdmin = (error.graphQLErrors as any)?.[0]?.extensions?.details?.group?.includes(
        "last_admin"
      )

      toast({
        title: "Error",
        description: isLastAdmin
          ? "You are the last admin in this group. You need to promote another user to be admin or remove all members before you can leave."
          : error.message,
        variant: "destructive",
      })
    },
  })

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="container py-6">
        <GraphqlError error={error} />
      </div>
    )
  }

  invariant(data)

  return (
    <div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:h-full">
        {data?.groups.edges
          .map((g) => g.node)
          .map((group) => <GroupIndexItem key={group.id} group={group} />)}
      </div>
      {data?.groups.pageInfo.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => {
              fetchMore({
                variables: {
                  after: data.groups.pageInfo.endCursor,
                },
              })
            }}
          >
            Load more
          </Button>
        </div>
      )}
      <Dialog open={!!leaveGroupDialog} onOpenChange={() => setLeaveGroupDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure you want to leave group:</DialogTitle>
          </DialogHeader>
          <div className="rounded-md bg-gray-100 p-4">{leaveGroupDialog?.name}</div>
          <div className="flex flex-col gap-2">
            <Button
              variant="default"
              className="w-full"
              onClick={async () => {
                if (!leaveGroupDialog) return

                const { errors } = await leaveGroup({
                  variables: {
                    input: {
                      groupId: leaveGroupDialog.id,
                    },
                  },
                })

                if (!errors) {
                  toast({
                    title: "Success",
                    description: `Left group "${leaveGroupDialog.name}"`,
                  })
                  setLeaveGroupDialog(null)
                }
              }}
            >
              Leave Group
            </Button>
            <DialogClose asChild>
              <Button variant="ghost" className="w-full">
                Close &amp; Cancel
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
